import React from 'react';
import { format, isAfter, isBefore, isSameDay, subMonths, isValid, startOfDay } from 'date-fns';
import { MessageSquare, Send, Clock, CheckCircle, XCircle, AlertTriangle, Book, Calendar, DollarSign, PiggyBank, Plus, Trash2, ArrowDownCircle, TrendingUp, Target, ArrowUpCircle, ArrowDownToLine, ArrowUpFromLine, Edit2 } from 'lucide-react';
import { collection, addDoc, query, where, Timestamp, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { useCollection } from '../hooks/useCollection';
import useEvents from '../hooks/useEvents';
import { Link } from 'react-router-dom';
import { addEvent } from '../lib/firebase';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (amount: number, direction: 'to-savings' | 'from-savings') => Promise<void>;
}

interface UpdateBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  onUpdate: (newBalance: number) => Promise<void>;
}

interface SavingsAdjustment {
  id: string;
  userId: string;
  amount: number;
  previousBalance: number;
  newBalance: number;
  date: Date;
  type: 'adjustment';
}

function UpdateBalanceModal({ isOpen, onClose, currentBalance, onUpdate }: UpdateBalanceModalProps) {
  const [newBalance, setNewBalance] = React.useState(currentBalance.toString());
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBalance) return;

    setLoading(true);
    setError(null);
    try {
      await onUpdate(parseFloat(newBalance));
      onClose();
    } catch (error) {
      setError('Failed to update balance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Update Savings Balance</h3>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              {error}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Balance</label>
            <p className="text-sm text-gray-500 mb-4">${currentBalance.toFixed(2)}</p>
            
            <label className="block text-sm font-medium text-gray-700">New Balance</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
                required
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              This will update your savings balance without affecting your calendar.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Balance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TransferModal({ isOpen, onClose, onTransfer }: TransferModalProps) {
  const [amount, setAmount] = React.useState('');
  const [direction, setDirection] = React.useState<'to-savings' | 'from-savings'>('to-savings');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount) return;

    setLoading(true);
    setError(null);
    try {
      await onTransfer(parseFloat(amount), direction);
      setAmount('');
      onClose();
    } catch (error) {
      setError('Failed to process transfer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer Money</h3>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              {error}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Transfer Direction</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDirection('to-savings')}
                className={`flex items-center justify-center px-4 py-2 border rounded-md ${
                  direction === 'to-savings'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-700'
                }`}
              >
                <ArrowUpCircle className="w-4 h-4 mr-2" />
                To Savings
              </button>
              <button
                type="button"
                onClick={() => setDirection('from-savings')}
                className={`flex items-center justify-center px-4 py-2 border rounded-md ${
                  direction === 'from-savings'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-700'
                }`}
              >
                <ArrowDownCircle className="w-4 h-4 mr-2" />
                From Savings
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SavingsTracker() {
  const { user } = useAuth();
  const { events } = useEvents();
  const [showTransferModal, setShowTransferModal] = React.useState(false);
  const [showUpdateModal, setShowUpdateModal] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  // Query for savings adjustments
  const adjustmentsQuery = React.useMemo(() => {
    if (!user?.uid) return null;
    return query(
      collection(db, 'savingsAdjustments'),
      where('userId', '==', user.uid)
    );
  }, [user?.uid]);

  const { data: adjustments } = useCollection<SavingsAdjustment>(adjustmentsQuery);

  const handleTransfer = async (amount: number, direction: 'to-savings' | 'from-savings') => {
    if (!user) return;

    try {
      const transferAmount = direction === 'to-savings' ? amount : -amount;
      const title = direction === 'to-savings' ? 'Transfer to Savings' : 'Transfer from Savings';
      
      // Create a bill event for the transfer
      await addEvent({
        id: crypto.randomUUID(),
        title,
        amount: Math.abs(transferAmount), // Always positive for display
        type: direction === 'to-savings' ? 'bill' : 'payday', // Bill reduces balance, payday increases it
        date: new Date(),
        category: 'savings',
        isPaid: true,
        paidDate: new Date()
      });

      setSuccess(`Successfully transferred $${amount.toFixed(2)} ${direction === 'to-savings' ? 'to' : 'from'} savings`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error processing transfer:', error);
      setError('Failed to process transfer. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleUpdateBalance = async (newBalance: number) => {
    if (!user) return;

    try {
      const currentBalance = calculateTotalSavings();
      
      // Create a savings adjustment record
      await addDoc(collection(db, 'savingsAdjustments'), {
        userId: user.uid,
        amount: newBalance - currentBalance,
        previousBalance: currentBalance,
        newBalance: newBalance,
        date: Timestamp.now(),
        type: 'adjustment'
      });

      setSuccess(`Successfully updated savings balance to $${newBalance.toFixed(2)}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating balance:', error);
      setError('Failed to update balance. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const getSavingsTransactions = () => {
    if (!events || !adjustments) return [];
    
    const today = startOfDay(new Date());
    
    // Combine calendar events and adjustments
    const allTransactions = [
      ...events
        .filter(event => 
          event.category === 'savings' && 
          event.isPaid &&
          isValid(event.date) // Add date validation
        )
        .map(event => ({
          ...event,
          isCalendarEvent: true
        })),
      ...adjustments.map(adj => ({
        id: adj.id,
        title: 'Manual Balance Adjustment',
        amount: adj.amount,
        date: adj.date instanceof Date ? adj.date : new Date(adj.date), // Ensure date is a Date object
        type: adj.amount >= 0 ? 'bill' : 'payday',
        isAdjustment: true
      }))
    ];
    
    // Filter out any transactions with invalid dates
    const validTransactions = allTransactions.filter(transaction => isValid(transaction.date));
    
    return validTransactions.sort((a, b) => b.date.getTime() - a.date.getTime());
  };

  const calculateTotalSavings = () => {
    if (!events || !adjustments) return 0;
    
    // Sum calendar events
    const calendarTotal = events
      .filter(event => event.category === 'savings' && event.isPaid)
      .reduce((total, event) => {
        if (event.type === 'bill') {
          return total + event.amount; // Adding to savings
        } else if (event.type === 'payday') {
          return total - event.amount; // Withdrawing from savings
        }
        return total;
      }, 0);

    // Add adjustments
    const adjustmentsTotal = adjustments.reduce((total, adj) => total + adj.amount, 0);

    return calendarTotal + adjustmentsTotal;
  };

  const calculateMonthlyAverage = () => {
    const transactions = getSavingsTransactions();
    if (transactions.length === 0) return 0;

    const sixMonthsAgo = subMonths(new Date(), 6);
    const recentTransactions = transactions.filter(t => 
      isValid(t.date) && isAfter(t.date, sixMonthsAgo)
    );
    
    return recentTransactions.reduce((total, t) => {
      if (t.type === 'bill') {
        return total + t.amount; // Count deposits
      }
      return total;
    }, 0) / 6;
  };

  const savingsTransactions = getSavingsTransactions();
  const totalSavings = calculateTotalSavings();
  const monthlyAverage = calculateMonthlyAverage();

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center">
            <PiggyBank className="w-6 h-6 mr-2" />
            Savings Tracker
          </h2>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowUpdateModal(true)}
              className="flex items-center px-4 py-2 glass-effect text-slate-700 rounded-lg hover:scale-105 transition-all duration-300"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Update Balance
            </button>
            <button
              onClick={() => setShowTransferModal(true)}
              className="flex items-center px-4 py-2 gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg cyber-glow"
            >
              <ArrowUpFromLine className="w-4 h-4 mr-2" />
              Transfer Money
            </button>
          </div>
        </div>

        {(error || success) && (
          <div className={`mb-6 p-4 rounded-md ${
            error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            <div className="flex">
              {error ? (
                <AlertTriangle className="h-5 w-5 mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 mr-2" />
              )}
              <p>{error || success}</p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Savings</p>
                <p className="text-3xl font-bold text-green-600">
                  ${totalSavings.toFixed(2)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Monthly Average (6mo)</p>
                <p className="text-3xl font-bold text-indigo-600">
                  ${monthlyAverage.toFixed(2)}
                </p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <TrendingUp className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Savings Transactions
            </h3>
          </div>
          <div className="border-t border-gray-200">
            <ul className="divide-y divide-gray-200">
              {savingsTransactions.map((transaction) => (
                <li key={transaction.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {transaction.type === 'bill' ? (
                        <ArrowUpCircle className="w-5 h-5 text-green-500 mr-3" />
                      ) : (
                        <ArrowDownCircle className="w-5 h-5 text-red-500 mr-3" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {transaction.title}
                          {transaction.isAdjustment && (
                            <span className="ml-2 text-xs text-gray-500">(Manual Update)</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">
                          {isValid(transaction.date) 
                            ? format(transaction.date, 'MMM d, yyyy')
                            : 'Invalid Date'}
                        </p>
                      </div>
                    </div>
                    <div className={`text-sm font-medium ${
                      transaction.type === 'bill' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'bill' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                    </div>
                  </div>
                </li>
              ))}

              {savingsTransactions.length === 0 && (
                <li className="px-4 py-12 text-center">
                  <PiggyBank className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No savings transactions yet
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Start saving by transferring money or setting up automatic savings on your paydays.
                  </p>
                </li>
              )}
            </ul>
          </div>
        </div>

        <TransferModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransfer}
        />

        <UpdateBalanceModal
          isOpen={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          currentBalance={totalSavings}
          onUpdate={handleUpdateBalance}
        />
      </div>
    </div>
  );
}